import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { isAdminAuthenticated, logoutAdmin } from '@/lib/adminAuth';
import InstagramContent from '@/pages/InstagramContent';
import BrandStudio from '@/components/zera/BrandStudio';
import {
  LogOut,
  Search,
  RefreshCw,
  Download,
  Mail,
  Phone,
  Calendar,
  Users,
  MapPin,
  Compass,
  Inbox,
  UserCheck,
  X,
  ChevronRight,
  TrendingUp,
  Sparkles,
  FileText,
  Instagram,
  Brain,
} from 'lucide-react';


type TabKey = 'journey' | 'booking' | 'newsletter' | 'profiles' | 'instagram' | 'brand_studio';


interface JourneyRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  destination: string;
  experience: string;
  check_in: string | null;
  check_out: string | null;
  guests: string;
  budget: string;
  special_requests: string;
  status: string;
  created_at: string;
}

interface BookingRow {
  id: string;
  experience: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  arrival_date: string | null;
  departure_date: string | null;
  guests: string;
  accommodation: string;
  budget: string;
  interests: string[] | null;
  special_requests: string;
  status: string;
  created_at: string;
}

interface NewsletterRow {
  id: string;
  email: string;
  source: string;
  created_at: string;
}

interface ProfileRow {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  travel_style: string;
  preferred_budget: string;
  dietary_requirements: string;
  special_notes: string;
  created_at: string;
}

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d;
  }
};

const fmtDateTime = (d?: string | null) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
};

const downloadCSV = (rows: Record<string, unknown>[], filename: string) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = Array.isArray(v) ? v.join('; ') : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Read initial tab from URL: ?tab=brand_studio or path-based shortcuts
  const initialTab: TabKey = (() => {
    const params = new URLSearchParams(location.search);
    const qTab = params.get('tab') as TabKey | null;
    if (qTab && ['journey', 'booking', 'newsletter', 'profiles', 'instagram', 'brand_studio'].includes(qTab)) return qTab;
    if (location.pathname.includes('brand-studio') || location.pathname.includes('brand_studio')) return 'brand_studio';
    if (location.pathname.includes('instagram')) return 'instagram';
    return 'journey';
  })();

  const [tab, setTab] = useState<TabKey>(initialTab);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);

  const [journeys, setJourneys] = useState<JourneyRow[]>([]);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [newsletter, setNewsletter] = useState<NewsletterRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/mtnadmin', { replace: true });
    }
  }, [navigate]);


  const fetchAll = async () => {
    setLoading(true);
    const [j, b, n, p] = await Promise.all([
      supabase.from('journey_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('booking_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('newsletter_subscribers').select('*').order('created_at', { ascending: false }),
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
    ]);
    if (j.data) setJourneys(j.data as JourneyRow[]);
    if (b.data) setBookings(b.data as BookingRow[]);
    if (n.data) setNewsletter(n.data as NewsletterRow[]);
    if (p.data) setProfiles(p.data as ProfileRow[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/mtnadmin', { replace: true });
  };

  const updateJourneyStatus = async (id: string, status: string) => {
    await supabase.from('journey_requests').update({ status }).eq('id', id);
    setJourneys((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const updateBookingStatus = async (id: string, status: string) => {
    await supabase.from('booking_requests').update({ status }).eq('id', id);
    setBookings((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  // Filtered data
  const q = search.trim().toLowerCase();
  const match = (s?: string | null) => !q || (s || '').toLowerCase().includes(q);

  const filteredJourneys = useMemo(
    () =>
      journeys.filter(
        (r) =>
          match(r.first_name) ||
          match(r.last_name) ||
          match(r.email) ||
          match(r.destination) ||
          match(r.experience) ||
          match(r.status),
      ),
    [journeys, q],
  );

  const filteredBookings = useMemo(
    () =>
      bookings.filter(
        (r) =>
          match(r.first_name) ||
          match(r.last_name) ||
          match(r.email) ||
          match(r.experience) ||
          match(r.accommodation) ||
          match(r.status),
      ),
    [bookings, q],
  );

  const filteredNewsletter = useMemo(
    () => newsletter.filter((r) => match(r.email) || match(r.source)),
    [newsletter, q],
  );

  const filteredProfiles = useMemo(
    () =>
      profiles.filter(
        (r) => match(r.first_name) || match(r.last_name) || match(r.travel_style),
      ),
    [profiles, q],
  );

  // Stats
  const newToday = (rows: { created_at: string }[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows.filter((r) => new Date(r.created_at) >= today).length;
  };

  const stats = [
    {
      key: 'journey',
      label: 'Journey Requests',
      total: journeys.length,
      today: newToday(journeys),
      icon: Compass,
    },
    {
      key: 'booking',
      label: 'Booking Requests',
      total: bookings.length,
      today: newToday(bookings),
      icon: Inbox,
    },
    {
      key: 'newsletter',
      label: 'Newsletter',
      total: newsletter.length,
      today: newToday(newsletter),
      icon: Mail,
    },
    {
      key: 'profiles',
      label: 'Registered Users',
      total: profiles.length,
      today: newToday(profiles),
      icon: UserCheck,
    },
    {
      key: 'instagram',
      label: 'Instagram Content',
      total: 7,
      today: 0,
      icon: Instagram,
    },
    {
      key: 'brand_studio',
      label: 'Brand Studio',
      total: 3,
      today: 0,
      icon: Brain,
    },
  ] as const;



  const statusBadge = (status: string) => {
    const s = (status || 'new').toLowerCase();
    const colors: Record<string, string> = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      contacted: 'bg-amber-50 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      closed: 'bg-slate-100 text-slate-600 border-slate-200',
      archived: 'bg-slate-100 text-slate-500 border-slate-200',
    };
    const cls = colors[s] || colors.new;
    return (
      <span
        className={`inline-flex font-sans text-[10px] tracking-wider uppercase px-2.5 py-0.5 border rounded-full ${cls}`}
      >
        {s}
      </span>
    );
  };

  const exportCurrent = () => {
    if (tab === 'journey') downloadCSV(filteredJourneys as any, 'journey_requests.csv');
    if (tab === 'booking') downloadCSV(filteredBookings as any, 'booking_requests.csv');
    if (tab === 'newsletter') downloadCSV(filteredNewsletter as any, 'newsletter_subscribers.csv');
    if (tab === 'profiles') downloadCSV(filteredProfiles as any, 'profiles.csv');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-[#0D1B2A] border-b border-[#C9A961]/20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#C9A961]" />
            </div>
            <div>
              <h1 className="font-serif text-lg text-white font-light">
                Zera Admin <span className="text-[#C9A961] text-xs tracking-widest uppercase ml-2">CRM</span>
              </h1>
              <p className="font-sans text-[10px] text-white/40 tracking-wider">
                Internal operations dashboard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setTab('brand_studio')}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors flex items-center gap-2 ${
                tab === 'brand_studio'
                  ? 'bg-[#C9A961] text-[#0D1B2A] border-[#C9A961]'
                  : 'border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A]'
              }`}
            >
              <Brain className="w-3.5 h-3.5" /> Brand Studio
            </button>
            <button
              onClick={() => setTab('instagram')}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors flex items-center gap-2 ${
                tab === 'instagram'
                  ? 'bg-[#C9A961] text-[#0D1B2A] border-[#C9A961]'
                  : 'border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A]'
              }`}
            >
              <Instagram className="w-3.5 h-3.5" /> Instagram Content
            </button>

            <button
              onClick={() => navigate('/admin/ai-tools')}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Social Tools
            </button>
            <button
              onClick={() => navigate('/admin/content-planner')}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-white/15 text-white/70 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center gap-2"
            >
              <FileText className="w-3.5 h-3.5" /> Content Planner
            </button>
            <button
              onClick={fetchAll}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-white/15 text-white/70 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#C9A961]/10 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>



      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          {stats.map((s) => {
            const Icon = s.icon;
            const active = tab === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setTab(s.key as TabKey)}
                className={`text-left bg-white border transition-all p-5 ${
                  active
                    ? 'border-[#C9A961] shadow-sm ring-1 ring-[#C9A961]/20'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className={`w-10 h-10 rounded flex items-center justify-center ${
                      active ? 'bg-[#C9A961] text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  {s.today > 0 && (
                    <span className="inline-flex items-center gap-1 font-sans text-[10px] tracking-wider uppercase text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <TrendingUp className="w-3 h-3" /> {s.today} today
                    </span>
                  )}
                </div>
                <div className="font-serif text-3xl text-[#0D1B2A] font-light">{s.total}</div>
                <div className="font-sans text-[11px] tracking-[0.2em] uppercase text-slate-500 mt-1">
                  {s.label}
                </div>
              </button>
            );
          })}
        </div>

        {tab === 'brand_studio' ? (
          /* Brand Learning Studio — 3-step social posting engine */
          <div className="bg-white border border-slate-200 overflow-hidden">
            <BrandStudio />
          </div>
        ) : tab === 'instagram' ? (
          /* Instagram Content — full-bleed embedded view inside the admin shell */
          <div className="bg-white border border-slate-200 overflow-hidden">
            <div className="bg-[#0D1B2A] px-6 py-4 flex items-center justify-between flex-wrap gap-3 border-b border-[#C9A961]/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
                  <Instagram className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div>
                  <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
                    @zeratravel · content calendar
                  </div>
                  <h2 className="font-serif text-lg text-white font-light">
                    A Week in the Life of Zera
                  </h2>
                </div>
              </div>
              <a
                href="/instagram-content"
                target="_blank"
                rel="noopener noreferrer"
                className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors"
              >
                Open Full View ↗
              </a>
            </div>
            <InstagramContent embedded />
          </div>
        ) : (

          <>
            {/* Toolbar */}
            <div className="bg-white border border-slate-200 p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search name, email, destination..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 pl-10 pr-4 py-2.5 font-sans text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#C9A961] transition-colors"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400 mr-2">
                  Showing{' '}
                  {tab === 'journey' && filteredJourneys.length}
                  {tab === 'booking' && filteredBookings.length}
                  {tab === 'newsletter' && filteredNewsletter.length}
                  {tab === 'profiles' && filteredProfiles.length}{' '}
                  records
                </span>
                <button
                  onClick={exportCurrent}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="py-24 text-center font-sans text-sm text-slate-400">
                  Loading data...
                </div>
              ) : (
                <>
                  {tab === 'journey' && (
                    <JourneyTable
                      rows={filteredJourneys}
                      onSelect={setSelected}
                      statusBadge={statusBadge}
                    />
                  )}
                  {tab === 'booking' && (
                    <BookingTable
                      rows={filteredBookings}
                      onSelect={setSelected}
                      statusBadge={statusBadge}
                    />
                  )}
                  {tab === 'newsletter' && <NewsletterTable rows={filteredNewsletter} />}
                  {tab === 'profiles' && <ProfilesTable rows={filteredProfiles} />}
                </>
              )}
            </div>
          </>
        )}
      </div>


      {/* Detail Drawer */}
      {selected && (
        <DetailDrawer
          record={selected}
          tab={tab}
          onClose={() => setSelected(null)}
          onUpdateStatus={(status) => {
            if (tab === 'journey') updateJourneyStatus(selected.id, status);
            if (tab === 'booking') updateBookingStatus(selected.id, status);
          }}
          statusBadge={statusBadge}
        />
      )}
    </div>
  );
};

// ======= Sub tables =======

const TH: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th
    className={`font-sans text-[10px] tracking-[0.2em] uppercase text-slate-500 font-medium text-left px-4 py-3 border-b border-slate-200 ${
      className || ''
    }`}
  >
    {children}
  </th>
);

const TD: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <td className={`px-4 py-3 font-sans text-sm text-slate-700 border-b border-slate-100 ${className || ''}`}>
    {children}
  </td>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="py-24 text-center">
    <p className="font-sans text-sm text-slate-400">{label}</p>
  </div>
);

interface TableProps<T> {
  rows: T[];
  onSelect: (r: T) => void;
  statusBadge: (s: string) => React.ReactNode;
}

const JourneyTable: React.FC<TableProps<JourneyRow>> = ({ rows, onSelect, statusBadge }) => {
  if (!rows.length) return <EmptyState label="No journey requests match your search." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <TH>Guest</TH>
            <TH>Contact</TH>
            <TH>Destination</TH>
            <TH>Dates</TH>
            <TH>Guests</TH>
            <TH>Budget</TH>
            <TH>Status</TH>
            <TH>Submitted</TH>
            <TH> </TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r)}
              className="hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <TD>
                <div className="font-medium text-[#0D1B2A]">
                  {r.first_name} {r.last_name}
                </div>
              </TD>
              <TD>
                <div className="text-xs text-slate-600">{r.email}</div>
                {r.phone && <div className="text-xs text-slate-400">{r.phone}</div>}
              </TD>
              <TD>
                <div className="text-xs">{r.destination || '—'}</div>
                {r.experience && <div className="text-[11px] text-slate-400">{r.experience}</div>}
              </TD>
              <TD className="whitespace-nowrap text-xs">
                {fmtDate(r.check_in)} to {fmtDate(r.check_out)}
              </TD>
              <TD className="text-xs">{r.guests || '—'}</TD>
              <TD className="text-xs">{r.budget || '—'}</TD>
              <TD>{statusBadge(r.status)}</TD>
              <TD className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(r.created_at)}</TD>
              <TD>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BookingTable: React.FC<TableProps<BookingRow>> = ({ rows, onSelect, statusBadge }) => {
  if (!rows.length) return <EmptyState label="No booking requests match your search." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <TH>Guest</TH>
            <TH>Contact</TH>
            <TH>Experience</TH>
            <TH>Dates</TH>
            <TH>Guests</TH>
            <TH>Accommodation</TH>
            <TH>Status</TH>
            <TH>Submitted</TH>
            <TH> </TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r)}
              className="hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <TD>
                <div className="font-medium text-[#0D1B2A]">
                  {r.first_name} {r.last_name}
                </div>
              </TD>
              <TD>
                <div className="text-xs text-slate-600">{r.email}</div>
                {r.phone && <div className="text-xs text-slate-400">{r.phone}</div>}
              </TD>
              <TD className="text-xs">{r.experience || '—'}</TD>
              <TD className="whitespace-nowrap text-xs">
                {fmtDate(r.arrival_date)} to {fmtDate(r.departure_date)}
              </TD>
              <TD className="text-xs">{r.guests || '—'}</TD>
              <TD className="text-xs">{r.accommodation || '—'}</TD>
              <TD>{statusBadge(r.status)}</TD>
              <TD className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(r.created_at)}</TD>
              <TD>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const NewsletterTable: React.FC<{ rows: NewsletterRow[] }> = ({ rows }) => {
  if (!rows.length) return <EmptyState label="No newsletter subscribers yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <TH>Email</TH>
            <TH>Source</TH>
            <TH>Subscribed</TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 transition-colors">
              <TD>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#C9A961]" />
                  <span className="font-medium text-[#0D1B2A]">{r.email}</span>
                </div>
              </TD>
              <TD className="text-xs">{r.source || 'footer'}</TD>
              <TD className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(r.created_at)}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProfilesTable: React.FC<{ rows: ProfileRow[] }> = ({ rows }) => {
  if (!rows.length) return <EmptyState label="No registered users yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <TH>Name</TH>
            <TH>Phone</TH>
            <TH>Travel Style</TH>
            <TH>Preferred Budget</TH>
            <TH>Dietary</TH>
            <TH>Registered</TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 transition-colors">
              <TD>
                <div className="font-medium text-[#0D1B2A]">
                  {r.first_name || '—'} {r.last_name || ''}
                </div>
                <div className="text-[11px] text-slate-400">{r.id.slice(0, 8)}...</div>
              </TD>
              <TD className="text-xs">{r.phone || '—'}</TD>
              <TD className="text-xs">{r.travel_style || '—'}</TD>
              <TD className="text-xs">{r.preferred_budget || '—'}</TD>
              <TD className="text-xs">{r.dietary_requirements || '—'}</TD>
              <TD className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(r.created_at)}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ======= Detail Drawer =======

const DetailRow: React.FC<{ label: string; value?: React.ReactNode; icon?: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100">
    {icon && <div className="text-[#C9A961] mt-0.5">{icon}</div>}
    <div className="flex-1">
      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-1">
        {label}
      </div>
      <div className="font-sans text-sm text-slate-800">{value || '—'}</div>
    </div>
  </div>
);

interface DrawerProps {
  record: any;
  tab: TabKey;
  onClose: () => void;
  onUpdateStatus: (status: string) => void;
  statusBadge: (s: string) => React.ReactNode;
}

const DetailDrawer: React.FC<DrawerProps> = ({ record, tab, onClose, onUpdateStatus, statusBadge }) => {
  const isJourney = tab === 'journey';
  const isBooking = tab === 'booking';
  const hasStatus = isJourney || isBooking;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <div className="sticky top-0 bg-[#0D1B2A] text-white px-6 py-5 flex items-center justify-between border-b border-[#C9A961]/20 z-10">
          <div>
            <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
              {isJourney ? 'Journey Request' : isBooking ? 'Booking Request' : 'Record'}
            </span>
            <h3 className="font-serif text-xl font-light mt-1">
              {record.first_name} {record.last_name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded border border-white/15 flex items-center justify-center hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {hasStatus && (
            <div className="mb-6">
              <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-2">
                Status
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {['new', 'contacted', 'confirmed', 'closed'].map((s) => (
                  <button
                    key={s}
                    onClick={() => onUpdateStatus(s)}
                    className={`font-sans text-[10px] tracking-wider uppercase px-3 py-1.5 border transition-all ${
                      (record.status || 'new') === s
                        ? 'border-[#C9A961] bg-[#C9A961] text-white'
                        : 'border-slate-200 text-slate-500 hover:border-[#C9A961]'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <DetailRow label="Email" value={record.email} icon={<Mail className="w-4 h-4" />} />
          <DetailRow label="Phone" value={record.phone} icon={<Phone className="w-4 h-4" />} />

          {isJourney && (
            <>
              <DetailRow
                label="Destination"
                value={record.destination}
                icon={<MapPin className="w-4 h-4" />}
              />
              <DetailRow label="Experience" value={record.experience} icon={<Compass className="w-4 h-4" />} />
              <DetailRow
                label="Check-in"
                value={fmtDate(record.check_in)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <DetailRow
                label="Check-out"
                value={fmtDate(record.check_out)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <DetailRow label="Guests" value={record.guests} icon={<Users className="w-4 h-4" />} />
              <DetailRow label="Budget" value={record.budget} />
              <DetailRow label="Special Requests" value={record.special_requests} />
            </>
          )}

          {isBooking && (
            <>
              <DetailRow
                label="Experience"
                value={record.experience}
                icon={<Compass className="w-4 h-4" />}
              />
              <DetailRow
                label="Arrival"
                value={fmtDate(record.arrival_date)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <DetailRow
                label="Departure"
                value={fmtDate(record.departure_date)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <DetailRow label="Guests" value={record.guests} icon={<Users className="w-4 h-4" />} />
              <DetailRow label="Accommodation" value={record.accommodation} />
              <DetailRow label="Budget" value={record.budget} />
              <DetailRow
                label="Interests"
                value={
                  Array.isArray(record.interests) && record.interests.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {record.interests.map((i: string) => (
                        <span
                          key={i}
                          className="font-sans text-[10px] tracking-wider uppercase px-2 py-0.5 bg-slate-100 text-slate-600 rounded"
                        >
                          {i}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '—'
                  )
                }
              />
              <DetailRow label="Special Requests" value={record.special_requests} />
            </>
          )}

          <DetailRow
            label="Submitted"
            value={fmtDateTime(record.created_at)}
            icon={<Calendar className="w-4 h-4" />}
          />

          <div className="pt-6 flex items-center gap-3">
            <a
              href={`mailto:${record.email}`}
              className="flex-1 font-sans text-[10px] tracking-[0.25em] uppercase px-6 py-3 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center justify-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" /> Email Guest
            </a>
            {record.phone && (
              <a
                href={`tel:${record.phone}`}
                className="font-sans text-[10px] tracking-[0.25em] uppercase px-6 py-3 border border-slate-200 text-slate-700 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
